import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {
    color,
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {devUsers, OAuthButtons} from "@khan/auth-login";
// new login flow
import {
    getLoginErrorMessageByCode,
    loginWithPassword,
} from "@khan/auth-password";
import {calculateNextUrl, type Role} from "@khan/auth-shared";
import {getPass} from "@khan/data-cookies";
import {useGql} from "@khan/data-gql";
import {doNotTranslate, doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {
    getContinueUrl,
    getSignupUrl,
    preserveMarketingParams,
    preserveSearchParams,
    redirect,
    useLocation,
} from "@khan/urls";

import AuthTitle from "../auth-title.tsx";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";
import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

const getErrorMsgs = () => ({
    oldIdentifierMissing: t`An email or username is required to log in. You can also log in with Google or Facebook.`,
    identifierMissing: t`An email or username is required to log in. You can also log in with Google, Facebook, Apple, or Clever.`,
    enIdentifierMissing:
        "An email or username is required to log in. " +
        "You can also log in with Google, Facebook, Apple, or Clever.",
    passwordMissing: t`A password is required to log in.`,
});

const StyledSpan = addStyle("span");

type Credentials<T> = {
    identifier: T;
    password: T;
    twoFactorAuthCode?: T;
    rememberTwoFactor?: boolean;
};

type Props = {
    classCode?: string;
    continueUrl?: string;
    role?: Role;
    renderSignup?: boolean;
    showOAuth?: boolean;
    loginCall?: (identifier: string, password: string) => Promise<void>;
    /**
     * The html tag to use for the login title.
     */
    loginTitleTag?: string;
};

type State = {
    credentials: Partial<Credentials<string | null>>;
    serverError: string | null | undefined;
    isSubmitting: boolean;
    twoFactorStep: boolean;
};

type Action =
    | {
          type: "ERROR";
          error: string | null | undefined;
      }
    | {
          type: "LOGIN";
      }
    | {
          type: "USERNAME";
          username: string;
      }
    | {
          type: "PASSWORD";
          password: string;
      }
    | {
          type: "TWO_FACTOR_STEP";
      }
    | {
          type: "TWO_FACTOR_AUTH_CODE";
          code: string;
      }
    | {
          type: "TWO_FACTOR_CODE_INCORRECT";
          error: string;
      };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "ERROR":
            return {
                ...state,
                isSubmitting: false,
                serverError: action.error,
            };
        case "LOGIN":
            return {
                ...state,
                isSubmitting: true,
                serverError: null,
            };
        case "USERNAME":
            return {
                ...state,
                credentials: {
                    ...state.credentials,
                    identifier: action.username,
                },
            };
        case "PASSWORD":
            return {
                ...state,
                credentials: {
                    ...state.credentials,
                    password: action.password,
                },
            };
        case "TWO_FACTOR_STEP":
            return {
                ...state,
                isSubmitting: false,
                twoFactorStep: true,
            };
        case "TWO_FACTOR_AUTH_CODE":
            return {
                ...state,
                credentials: {
                    ...state.credentials,
                    twoFactorAuthCode: action.code,
                },
            };
        case "TWO_FACTOR_CODE_INCORRECT":
            return {
                ...state,
                isSubmitting: false,
                // reset the credentials
                credentials: {},
                // kick back to the login form if the code is incorrect
                twoFactorStep: false,
                serverError: action.error,
            };
        default:
            return state;
    }
};

const LoginForm = (props: Props): React.ReactElement | null => {
    let {continueUrl = null, showOAuth = true} = props;
    const {renderSignup = true} = props;
    const gqlFetch = useGql();
    const location = useLocation();
    const initialState: State = {
        credentials: {},
        serverError: null,
        isSubmitting: false,
        twoFactorStep: false,
    };
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const usernameInputField = React.useRef<HTMLInputElement | null>(null);

    const doLogin: (credentials: Credentials<string>) => void = (
        credentials,
    ) => {
        const {classCode} = props;
        const {isSubmitting} = state;
        if (isSubmitting) {
            return;
        }

        dispatch({type: "LOGIN"});

        continueUrl = getContinueUrl(location) ?? continueUrl;

        const defaultLoginCall = (identifier: string, password: string) =>
            loginWithPassword(gqlFetch, {
                identifier,
                password,
                signupCodes: classCode ? [classCode] : undefined,
                twoFactorAuthCode: credentials.twoFactorAuthCode,
                rememberTwoFactor: credentials.rememberTwoFactor,
            }).then((userData) => {
                calculateNextUrl({
                    gqlFetch,
                    location,
                    loginType: "password",
                    continueUrl: continueUrl || "/",
                    isFirstLogin: userData.isFirstLogin,
                    joinClassrooms: userData.joinClassrooms,
                }).then((calculatedNextUrl) => {
                    redirect(
                        preserveMarketingParams(
                            calculatedNextUrl,
                            new URLSearchParams(location.search),
                        ),
                    );
                });
            });

        const loginCall = props.loginCall ?? defaultLoginCall;

        loginCall(credentials.identifier, credentials.password).catch(
            (error) => {
                // We refocus the user on the username field so it's faster to
                // fix it if that was the issue.
                if (usernameInputField.current) {
                    usernameInputField.current.focus();
                }

                // This is a signal that two factor authentication is enabled,
                // and we need to show the 2FA form so users can input their
                // verification codes.
                if (error.message === "TWO_FACTOR_CODE_REQUIRED") {
                    dispatch({type: "TWO_FACTOR_STEP"});
                    return;
                }

                const errorMessage = getLoginErrorMessageByCode(error.message);
                // This is a signal that the two factor code is incorrect,
                // and we should kick back to the login form.
                if (error.message === "TWO_FACTOR_CODE_INCORRECT") {
                    dispatch({
                        type: "TWO_FACTOR_CODE_INCORRECT",
                        error: errorMessage,
                    });
                    return;
                }

                dispatch({type: "ERROR", error: errorMessage});
            },
        );
    };

    const getCredentials = (): Credentials<string> | null | undefined => {
        const {identifier, password, twoFactorAuthCode} = state.credentials;
        if (
            state.twoFactorStep &&
            twoFactorAuthCode &&
            identifier &&
            password
        ) {
            return {
                identifier,
                password,
                // in case people put a space in the middle of the code
                twoFactorAuthCode: twoFactorAuthCode.replace(" ", ""),
                rememberTwoFactor: false,
            };
        }
        if (identifier && password) {
            return {identifier, password};
        }
        return null;
    };

    const handleDevSubmit: (selectedValue: string) => void = (
        selectedValue,
    ) => {
        const parts = selectedValue.split(":");
        const identifier = parts[0];
        const password = parts.slice(1).join(":");

        doLogin({identifier, password});
    };

    const handleSubmit: (e: React.SyntheticEvent) => void = (e) => {
        // NOTE(diedra): The button is not disabled while submitting the form
        // so the user doesn't lose focus if they tabbed to the button to submit the form.
        if (state.isSubmitting) {
            return;
        }

        // TODO(ian): We should preventDefault inside the TextField component
        e.preventDefault();
        const credentials = getCredentials();
        if (credentials) {
            doLogin(credentials);
        }
    };

    const renderLoginButtonText = (): string => {
        const {isSubmitting} = state;
        return isSubmitting ? t`Logging you in...` : t`Log in`;
    };

    const renderRequiredIndicatorDescriptor = (): React.ReactElement => {
        return (
            <LabelSmall>
                <Trans>
                    <StyledSpan style={styles.requiredIndicator}>*</StyledSpan>{" "}
                    indicates a required field.
                </Trans>
            </LabelSmall>
        );
    };

    const maybeRenderDevUsers = (): React.ReactElement | null => {
        if (!RequestInfo.IS_DEV_SERVER || getPass("use_prod_backend")) {
            return null;
        }

        // These are pre-filled users that are created when we create
        // current.sqlite: devUsers is a list of users that the
        // developer can log in as; each user is an object containing
        // their email address and bio.
        const userOptions = devUsers.map((user) => (
            <OptionItem
                key={user.username}
                label={`${user.username}: ${user.bio}`}
                value={`${user.username}:${user.password}`}
            />
        ));

        // Third-party login methods work in "frontend" mode so only show the tooltip
        // if we're in a different mode.
        const showTooltip = globalThis.__FASTLY_MODE__ !== "frontend";

        return (
            <View style={styles.devPanel}>
                <SingleSelect
                    aria-label="User"
                    onChange={handleDevSubmit}
                    placeholder="OR log in as an existing user:"
                >
                    {userOptions}
                </SingleSelect>
                <Strut size={spacing.small_12} />
                {showTooltip && (
                    <Tooltip
                        content={
                            doNotTranslate`NOTE: third-party login methods do not work in ` +
                            `dev, by default. To enable third-party logins, ` +
                            `uncomment the code in services/users/cmd/serve/main.go and ` +
                            `apps/khanacademy/src/shared/auth/login/login-form.tsx!`
                        }
                    >
                        <PhosphorIcon icon={infoIcon} color={color.blue} />
                    </Tooltip>
                )}
            </View>
        );
    };

    const renderSignupButton = () => (
        <LabelMedium tag="p">
            {t`Need a Khan Academy account?`}{" "}
            <Link
                href={preserveSearchParams(
                    getSignupUrl(),
                    new URLSearchParams(location.search),
                )}
                style={styles.signUpLink}
                target={RequestInfo.isExtension ? "_blank" : undefined}
            >
                {t`Create an account`}
            </Link>
        </LabelMedium>
    );

    const renderTwoFactorAuthForm = (): React.ReactElement => {
        const {serverError} = state;
        return (
            <>
                <AuthTitle>{doNotTranslateYet`Two-factor authentication`}</AuthTitle>
                <Strut size={spacing.large_24} />
                <LabelMedium>
                    {doNotTranslateYet`Use your authenticator app to enter the verification code.`}
                </LabelMedium>
                <form className={css(styles.column)} onSubmit={handleSubmit}>
                    <View
                        role="alert"
                        aria-live="polite"
                        style={styles.serverErrorContainer}
                    >
                        {serverError && (
                            <View style={styles.errorRow}>
                                <PhosphorIcon
                                    icon={warningCircleIcon}
                                    color={
                                        semanticColor.core.foreground.critical
                                            .default
                                    }
                                />
                                <Body tag="p" style={styles.errorMessage}>
                                    {serverError}
                                </Body>
                            </View>
                        )}
                    </View>
                    <LabeledTextField
                        testId="two-factor-code"
                        label={doNotTranslateYet`Verify your code:`}
                        value={state.credentials.twoFactorAuthCode || ""}
                        onChange={(code) => {
                            // 6 letters + 1 potential space
                            if (code.length <= 7) {
                                dispatch({
                                    type: "TWO_FACTOR_AUTH_CODE",
                                    code: code,
                                });
                            }
                        }}
                    />
                    <Strut size={spacing.small_12} />
                    <Button
                        type="submit"
                        disabled={
                            !state.credentials.twoFactorAuthCode ||
                            // in case people put a space in the middle of the code
                            state.credentials.twoFactorAuthCode.replace(" ", "")
                                .length !== 6
                        }
                        testId="log-in-submit-button"
                        style={styles.loginButton}
                    >
                        {renderLoginButtonText()}
                    </Button>
                    <Strut size={spacing.small_12} />
                </form>
            </>
        );
    };

    // For more information about handling a11y for form errors,
    // visit khanacademy.atlassian.net/l/c/m13J36pf.
    const renderLoginForm = (): React.ReactElement => {
        const {serverError} = state;
        const {role, continueUrl, classCode, loginTitleTag} = props;

        const errorMsgs = getErrorMsgs();
        // NOTE(FEI-3333): Once identifierMissing has been fully translated
        // we can remove these checks and just use that string.
        const {enIdentifierMissing, oldIdentifierMissing} = errorMsgs;
        let {identifierMissing} = errorMsgs;
        if (
            RequestInfo.kaLocale !== "en" &&
            identifierMissing === enIdentifierMissing
        ) {
            identifierMissing = oldIdentifierMissing;
        }

        showOAuth = RequestInfo.IS_DEV_SERVER
            ? showOAuth && globalThis.__FASTLY_MODE__ === "frontend"
            : showOAuth;

        return (
            <Id>
                {(uniqueId) => (
                    <>
                        <AuthTitle tag={loginTitleTag}>{t`Log in`}</AuthTitle>
                        {showOAuth && (
                            <OAuthButtons
                                continueUrl={continueUrl}
                                classCode={classCode}
                                role={role}
                                setServerError={(serverError: string) => {
                                    dispatch({
                                        type: "ERROR",
                                        error: serverError,
                                    });
                                }}
                            />
                        )}
                        {maybeRenderDevUsers()}
                        <Strut size={spacing.large_24} />
                        <form
                            className={css(styles.column)}
                            onSubmit={handleSubmit}
                        >
                            {renderRequiredIndicatorDescriptor()}
                            <View
                                role="alert"
                                aria-live="polite"
                                id={`${uniqueId}-server-error`}
                                style={styles.serverErrorContainer}
                            >
                                {serverError && (
                                    <View style={styles.errorRow}>
                                        <PhosphorIcon
                                            icon={warningCircleIcon}
                                            color={
                                                semanticColor.core.foreground
                                                    .critical.default
                                            }
                                        />
                                        <Body
                                            tag="p"
                                            style={styles.errorMessage}
                                        >
                                            {serverError}
                                        </Body>
                                    </View>
                                )}
                            </View>
                            <LabeledTextField
                                testId="identifier"
                                id="username"
                                name="username"
                                autoComplete="username"
                                label={t`Email or username`}
                                value={state.credentials.identifier || ""}
                                onChange={(newValue) =>
                                    dispatch({
                                        type: "USERNAME",
                                        username: newValue,
                                    })
                                }
                                ariaDescribedby={
                                    serverError != null
                                        ? `${uniqueId}-server-error`
                                        : undefined
                                }
                                required={identifierMissing}
                                ref={usernameInputField}
                            />
                            <Strut size={spacing.small_12} />
                            <LabeledTextField
                                id="current-password"
                                name="current-password"
                                autoComplete="current-password"
                                testId="password"
                                type="password"
                                label={t`Password`}
                                value={state.credentials.password || ""}
                                onChange={(newValue) =>
                                    dispatch({
                                        type: "PASSWORD",
                                        password: newValue,
                                    })
                                }
                                ariaDescribedby={
                                    serverError != null
                                        ? `${uniqueId}-server-error`
                                        : undefined
                                }
                                required={t`A password is required to log in.`}
                            />
                            <Link
                                href={
                                    RequestInfo.isExtension
                                        ? "/forgotpw?extension=1"
                                        : "/forgotpw"
                                }
                                style={styles.forgotPasswordLink}
                            >
                                {t`Forgot password?`}
                            </Link>
                            <Strut size={spacing.small_12} />
                            <Button
                                type="submit"
                                disabled={!getCredentials()}
                                testId="log-in-submit-button"
                                style={styles.loginButton}
                            >
                                {renderLoginButtonText()}
                            </Button>
                            <Strut size={spacing.small_12} />
                            {renderSignup && (
                                <>
                                    {renderSignupButton()}
                                    <Strut size={spacing.small_12} />
                                </>
                            )}
                        </form>
                    </>
                )}
            </Id>
        );
    };

    return (
        <>
            {state.twoFactorStep
                ? renderTwoFactorAuthForm()
                : renderLoginForm()}
        </>
    );
};

const styles = StyleSheet.create({
    serverErrorContainer: {
        minHeight: spacing.small_12,
    },
    errorRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_080,
    },
    errorMessage: {
        color: semanticColor.core.foreground.critical.default,
    },
    loginButton: {
        width: "100%",
    },
    column: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    devPanel: {
        flexDirection: "row",
        alignItems: "center",
        paddingTop: spacing.xSmall_8,
    },
    forgotPasswordLink: {
        marginBottom: spacing.small_12,
    },
    requiredIndicator: {
        color: color.red,
    },
    signUpLink: {
        fontWeight: "bold",
    },
});

export default LoginForm;
